import React, { useState, useRef, useEffect } from "react";
import styles from "./KanbanView.module.scss";
import classNames from "classnames/bind";
import { ActionIcon } from "~/assets/icons";
import dayjs from "dayjs";
const cx = classNames.bind(styles);

export interface KanbanViewProps {
  data: any;
  onJobMove?: (
    jobId: string,
    fromStage: string,
    toStage: string,
    job: any
  ) => Promise<void>;
  onJobClick?: (job: any) => void;
  onJobEdit?: (job: any) => void;
  onJobDelete?: (job: any) => void;
  rollbackInProgress?: boolean;
}

const KanbanView: React.FC<KanbanViewProps> = ({
  data,
  onJobMove,
  onJobClick,
  onJobEdit,
  onJobDelete,
  rollbackInProgress = false,
}) => {
  const [draggedItem, setDraggedItem] = useState<{
    job: any;
    sourceColumn: string;
  } | null>(null);
  const [dragOverColumn, setDragOverColumn] = useState<string | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const menuRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  const columns = Object.values(data);

  // Handle click outside to close menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openMenuId) {
        const menuElement = menuRefs.current[openMenuId];
        if (menuElement && !menuElement.contains(event.target as Node)) {
          setOpenMenuId(null);
        }
      }
    };

    if (openMenuId) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openMenuId]);

  // Helper function to normalize priority for CSS class
  const getPriorityClass = (priority: string | undefined): string => {
    if (!priority) return "";
    const upperPriority = priority.toUpperCase();
    if (upperPriority === "HIGH") return "High";
    if (upperPriority === "MEDIUM") return "Medium";
    if (upperPriority === "LOW") return "Low";
    return "";
  };

  // Helper function to get display text for priority
  const getPriorityText = (priority: string | undefined): string => {
    if (!priority) return "";
    return priority.toLowerCase();
  };

  const handleDragStart = (
    e: React.DragEvent,
    job: any,
    sourceColumn: string
  ) => {
    setDraggedItem({ job, sourceColumn });
    e.dataTransfer.effectAllowed = "move";
    (e.currentTarget as HTMLElement).style.opacity = "0.5";
  };

  const handleDragEnd = (e: React.DragEvent) => {
    (e.currentTarget as HTMLElement).style.opacity = "1";
    setDraggedItem(null);
    setDragOverColumn(null);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDragEnter = (columnStage: string) => {
    setDragOverColumn(columnStage);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    if (e.currentTarget === e.target) {
      setDragOverColumn(null);
    }
  };

  const handleDrop = async (e: React.DragEvent, destinationColumn: string) => {
    e.preventDefault();

    if (!draggedItem) return;

    const { job, sourceColumn } = draggedItem;

    // If dropped in the same column, do nothing
    if (sourceColumn === destinationColumn) {
      setDraggedItem(null);
      setDragOverColumn(null);
      return;
    }

    setDraggedItem(null);
    setDragOverColumn(null);

    // Call the parent callback to handle optimistic update and background API call
    if (onJobMove) {
      try {
        await onJobMove(job.id, sourceColumn, destinationColumn, job);
      } catch (error) {
        console.error("Failed to move job:", error);
      }
    }
  };

  return (
    <div className={cx("kanban-container")}>
      {rollbackInProgress && (
        <div className={cx("rollback-indicator")}>
          ⚠️ Rolling back changes...
        </div>
      )}
      {columns.map((column: any) => (
        <div
          key={column?.stage}
          className={cx("kanban-column")}
          onDragOver={handleDragOver}
          onDragEnter={() => handleDragEnter(column?.stage)}
          onDragLeave={handleDragLeave}
          onDrop={(e) => handleDrop(e, column?.stage)}
          style={{
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            border:
              dragOverColumn === column?.stage
                ? "2px dashed #4a9eff"
                : "2px solid transparent",
            transform:
              dragOverColumn === column?.stage ? "scale(1.02)" : "scale(1)",
          }}
        >
          <div className={cx("kanban-header")}>
            <h3>{column?.stage}</h3>
            <div className={cx("kanban-count-container")}>
              <p>{column?.jobs?.length || 0}</p>
            </div>
          </div>

          <div className={cx("kanban-content")}>
            {column?.jobs?.map((job: any) => (
              <div
                key={job.id}
                className={cx("kanban-card")}
                draggable
                onDragStart={(e) => handleDragStart(e, job, column?.stage)}
                onDragEnd={handleDragEnd}
                style={{
                  cursor: "grab",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                }}
                onClick={() => onJobClick && onJobClick(job)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-4px)";
                  e.currentTarget.style.boxShadow =
                    "0 8px 16px rgba(0, 0, 0, 0.15)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                <div className={cx("card-header")}>
                  <h4 className={cx("job-title")}>
                    {job?.title || "Untitled Project"}
                  </h4>

                  <div className={cx("right-header")}>
                    {job?.priority && (
                      <p className={cx(getPriorityClass(job?.priority))}>
                        {getPriorityText(job?.priority)}
                      </p>
                    )}

                    <div
                      ref={(el) => {
                        menuRefs.current[job.id] = el;
                      }}
                      className={cx("action-menu-container")}
                    >
                      <button
                        className={cx("kanban-button")}
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenMenuId(openMenuId === job.id ? null : job.id);
                        }}
                      >
                        <ActionIcon className={cx("action-icon")} />
                      </button>
                      {openMenuId === job.id && (
                        <>
                          <div
                            className={cx("dropdown-backdrop")}
                            onClick={() => setOpenMenuId(null)}
                          />
                          <div className={cx("action-dropdown")}>
                            <button
                              className={cx("dropdown-item")}
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenMenuId(null);
                                if (onJobEdit) {
                                  onJobEdit(job);
                                }
                              }}
                            >
                              Edit
                            </button>
                            <button
                              className={cx("dropdown-item", "dropdown-item-delete")}
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenMenuId(null);
                                if (onJobDelete) {
                                  onJobDelete(job);
                                }
                              }}
                            >
                              Delete
                            </button>
                            <button
                              className={cx("dropdown-item")}
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenMenuId(null);
                                if (onJobClick) {
                                  onJobClick(job);
                                }
                              }}
                            >
                              View Detail
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                <h3 className={cx("address")}>{job?.jobAddress}</h3>

                <div className={cx("materials-cell")}>
                  {job?.jobMaterials?.map((jobMaterial: any, index: number) => (
                    <p key={index} className={cx("material-name")}>
                      {jobMaterial}
                    </p>
                  ))}
                </div>

                <div className={cx("card-footer")}>
                  <div className={cx("assigned-account")}>
                    <div
                      className={cx("assigned-avatar")}
                      style={{
                        backgroundColor:
                          job?.assignedAccount?.color || "#C4C4C4",
                      }}
                    />
                    <p className={cx("assigned-username")}>
                      {job?.primaryCustomerName || ""}
                    </p>
                  </div>

                  <p className={cx("due-date")}>
                    Due: {job?.dueDate ? dayjs(job?.dueDate).format("MMM D, YYYY") : "No due date yet."}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default KanbanView;
